import { Loader, Tooltip, VisuallyHidden } from "@mantine/core";
import { Zap } from "lucide-react";

import type { JobQuote } from "../api/generated/model";
import { APPROXIMATE_HINT, creditsText } from "./credits";
import classes from "./Price.module.css";

/**
 * The cost chip, as live's `uft-estimate`: the most the draft can cost, with the whole sentence
 * for a screen reader. A firm cost is the plain figure on no fill, the caption of the button
 * beside it. Live's qualified style, a pale lavender fill with the icon and figure in the accent,
 * marks an approximate estimate (a video charged its delivered length: every source-length one,
 * Daniil, 2026-10-21), with live's hint, and a missing figure: the icon alone, a spinner while the
 * quote runs. The width eases as the figure changes.
 */
export function Price(props: {
  priced: JobQuote | undefined;
  pending: boolean;
  /** The sentence while there is no price: what the draft needs first. */
  unpriced?: string;
}) {
  const { priced, pending, unpriced = "Write a prompt to see the price." } = props;
  const sentence =
    priced === undefined
      ? unpriced
      : priced.approximate
        ? APPROXIMATE_HINT
        : `This costs ${creditsText(priced)}. You pay only for the outputs that are delivered; ` +
          `${priced.credits_max} credits are held while it runs.`;
  return (
    <Tooltip label={sentence} multiline w={260} withinPortal={false}>
      {/* An <output> is a status region: when the price arrives, its sentence is read out. */}
      <output
        className={classes.price}
        data-qualified={priced === undefined || priced.approximate || undefined}
        aria-label={sentence}
      >
        {pending ? <Loader size={16} aria-hidden /> : <Zap size={20} aria-hidden />}
        {priced === undefined ? null : <span aria-hidden>{priced.credits_max}</span>}
        <VisuallyHidden>{sentence}</VisuallyHidden>
      </output>
    </Tooltip>
  );
}
